import { isReportableFinding, type V2Finding } from '../domain/finding';
import {
  areaCompletionFor,
  progressFor,
  statusNeedsReason,
  type AreaCompletionSummary,
  type ProgressSummary,
  type V2Area,
  type V2ChecklistItem,
  type V2Inspection
} from '../domain/inspection';

// Inspection overview: a pure derivation over inspector-settled state. Report assembly and
// export (bilingual PDF, referral trades, legal clauses) are later TRUE V2 rounds — nothing
// here fakes a final report. Limitations (unable / not applicable rows with their reasons)
// are surfaced because expected items must never vanish silently (Frozen Spec §9).
export interface AreaOverview {
  area: V2Area;
  progress: ProgressSummary;
  findings: V2Finding[];
  limitations: V2ChecklistItem[];
}

export interface InspectionOverview {
  inspection: V2Inspection;
  totals: ProgressSummary;
  completion: AreaCompletionSummary;
  areas: AreaOverview[];
  findingCount: number;
}

export function buildInspectionOverview(
  inspection: V2Inspection,
  areas: readonly V2Area[],
  items: readonly V2ChecklistItem[],
  findings: readonly V2Finding[]
): InspectionOverview {
  const ownAreas = areas
    .filter((a) => a.inspectionId === inspection.id && a.enabled)
    .sort((a, b) => a.sortOrder - b.sortOrder);
  const ownItems = items.filter((i) => i.inspectionId === inspection.id);
  const ownFindings = findings.filter((f) => f.inspectionId === inspection.id && isReportableFinding(f));
  const areaOverviews = ownAreas.map((area) => {
    const areaItems = ownItems.filter((i) => i.areaId === area.id).sort((a, b) => a.sortOrder - b.sortOrder);
    return {
      area,
      progress: progressFor(areaItems),
      findings: ownFindings.filter((f) => f.areaId === area.id),
      limitations: areaItems.filter((i) => statusNeedsReason(i.status))
    };
  });
  const enabledAreaIds = new Set(ownAreas.map((a) => a.id));
  return {
    inspection,
    totals: progressFor(ownItems.filter((i) => enabledAreaIds.has(i.areaId))),
    completion: areaCompletionFor(areas.filter((a) => a.inspectionId === inspection.id)),
    areas: areaOverviews,
    findingCount: ownFindings.length
  };
}
